import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AdminConfigService } from '../../services/admin-config.service';
import { ToastNotificationComponent } from '../../toast-notification/toast-notification.component';
import { ListItem } from '../../models/ListItem';
import { AcsCommodity, AcsTradeUnitCommodity } from '../models/acs.models';
import { ACS_TOAST_SUCCESS_DURATION, ACS_TOAST_ERROR_DURATION } from '../utils/acs.constants';
import { extractErrorMessage, scopedLocsToListItems } from '../utils/acs.utils';
import { AcsDropdownService } from '../utils/acs-dropdown.service';
import { AcsDropdownState } from '../utils/acs-dropdown-state';

interface CommodityRow {
  commodityCode: string;
  shortDescription: string;
  description: string;
  uom: string;
  included: boolean;
}

@Component({
  selector: 'app-acs-trade-unit-commodity',
  templateUrl: './acs-trade-unit-commodity.component.html',
  styleUrls: ['../acs-shared.css'],
  standalone: false
})
export class AcsTradeUnitCommodityComponent implements OnInit, OnDestroy {

  private _destroy$ = new Subject<void>();

  @ViewChild('toastNotification') toastNotification!: ToastNotificationComponent;

  // Reference data
  locList: ListItem[] = [];
  allCommodities: AcsCommodity[] = [];

  // Loc selector
  locDropdown!: AcsDropdownState;

  // Commodity toggle state
  includedCommodityCodes: Set<string> = new Set();
  private _originalCommodityCodes: Set<string> = new Set();
  private _sortedRowOrder: string[] = [];

  // Search filter
  commoditySearchText: string = '';

  // State
  saving: boolean = false;
  loading: boolean = false;

  constructor(
    private _adminConfigService: AdminConfigService,
    public dropdowns: AcsDropdownService
  ) {
    this.locDropdown = new AcsDropdownState('tuc-loc', this.dropdowns, () => this.locList, '-- Select Trade Unit --');
  }

  ngOnInit(): void {
    this._adminConfigService.getScopedLocs().pipe(takeUntil(this._destroy$)).subscribe({
      next: (result) => this.locList = scopedLocsToListItems(result, false)
    });
    this._adminConfigService.getAllCommodities().pipe(takeUntil(this._destroy$)).subscribe({
      next: (data) => this.allCommodities = data
    });
    fromEvent(document, 'click').pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.dropdowns.close();
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  // --- Loc Selector ---

  toggleLocDropdown(): void {
    this.locDropdown.toggle();
  }

  selectLocOption(loc: ListItem | null): void {
    this.locDropdown.select(loc);
    if (this.locDropdown.selectedValue) {
      this.loadTradeUnitCommodities();
    } else {
      this.includedCommodityCodes.clear();
      this._originalCommodityCodes.clear();
    }
  }

  // --- Data Loading ---

  private loadTradeUnitCommodities(): void {
    this.loading = true;
    this._adminConfigService.getTradeUnitCommodities(this.locDropdown.selectedValue).pipe(takeUntil(this._destroy$)).subscribe({
      next: (data: AcsTradeUnitCommodity[]) => {
        this.includedCommodityCodes = new Set(data.map(lc => lc.commodityCode));
        this._originalCommodityCodes = new Set(this.includedCommodityCodes);
        this.buildSortOrder();
        this.loading = false;
      },
      error: () => {
        this.toastNotification.showToast('Failed to load commodity configuration', ACS_TOAST_ERROR_DURATION, 'error');
        this.loading = false;
      }
    });
  }

  // --- Commodity Rows ---

  private buildSortOrder(): void {
    const included: string[] = [];
    const excluded: string[] = [];

    for (const c of this.allCommodities) {
      if (this.includedCommodityCodes.has(c.commodityCode)) {
        included.push(c.commodityCode);
      } else {
        excluded.push(c.commodityCode);
      }
    }

    included.sort();
    excluded.sort();
    this._sortedRowOrder = [...included, ...excluded];
  }

  get commodityRows(): CommodityRow[] {
    const masterMap: Map<string, AcsCommodity> = new Map(this.allCommodities.map(c => [c.commodityCode, c]));
    return this._sortedRowOrder.map(code => {
      const master = masterMap.get(code);
      return {
        commodityCode: code,
        shortDescription: master?.shortDescription || '',
        description: master?.description || '',
        uom: master?.uom || '',
        included: this.includedCommodityCodes.has(code)
      };
    });
  }

  get filteredCommodityRows(): CommodityRow[] {
    const rows: CommodityRow[] = this.commodityRows;
    if (!this.commoditySearchText) return rows;
    const lower: string = this.commoditySearchText.toLowerCase();
    return rows.filter(r =>
      r.commodityCode.toLowerCase().includes(lower) ||
      (r.shortDescription || '').toLowerCase().includes(lower) ||
      (r.description || '').toLowerCase().includes(lower)
    );
  }

  get includedCount(): number {
    return this.includedCommodityCodes.size;
  }

  get totalCount(): number {
    return this.allCommodities.length;
  }

  get hasUnsavedChanges(): boolean {
    if (this.includedCommodityCodes.size !== this._originalCommodityCodes.size) return true;
    for (const code of this.includedCommodityCodes) {
      if (!this._originalCommodityCodes.has(code)) return true;
    }
    return false;
  }

  // --- Toggle ---

  toggleCommodity(commodityCode: string): void {
    if (this.includedCommodityCodes.has(commodityCode)) {
      this.includedCommodityCodes.delete(commodityCode);
    } else {
      this.includedCommodityCodes.add(commodityCode);
    }
    // Force change detection by creating new set
    this.includedCommodityCodes = new Set(this.includedCommodityCodes);
  }

  // --- Save ---

  saveCommodities(): void {
    if (!this.locDropdown.selectedValue || this.saving) return;

    this.saving = true;
    this._adminConfigService.saveTradeUnitCommodities(this.locDropdown.selectedValue, [...this.includedCommodityCodes])
      .pipe(takeUntil(this._destroy$)).subscribe({
        next: () => {
          this._originalCommodityCodes = new Set(this.includedCommodityCodes);
          this.buildSortOrder();
          this.toastNotification.showToast('Commodity configuration saved', ACS_TOAST_SUCCESS_DURATION, 'success');
          this.saving = false;
        },
        error: (err) => {
          const msg: string = extractErrorMessage(err, 'Failed to save commodity configuration');
          this.toastNotification.showToast(msg, ACS_TOAST_ERROR_DURATION, 'error');
          this.saving = false;
        }
      });
  }
}
